import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { AdminCleaner } from "@/mocks/admin";
import { allAdminBookings } from "@/mocks/admin";

type Props = {
  cleaner: AdminCleaner | null;
  onClose: () => void;
  onStatusChange: (next: AdminCleaner["status"]) => void;
  onRemove: () => void;
};

const statusPill: Record<AdminCleaner["status"], string> = {
  active: "bg-primary-100 text-primary-700",
  onboarding: "bg-accent-100 text-accent-700",
  on_leave: "bg-background-200 text-foreground-700",
};

const statusLabel: Record<AdminCleaner["status"], string> = {
  active: "Active",
  onboarding: "Onboarding",
  on_leave: "On leave",
};

export default function CleanerDrawer({
  cleaner,
  onClose,
  onStatusChange,
  onRemove,
}: Props) {
  useEffect(() => {
    if (!cleaner) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [cleaner, onClose]);

  if (!cleaner) return null;

  const recent = allAdminBookings
    .filter((b) => b.cleanerId === cleaner.id)
    .slice(0, 4);

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <aside className="absolute right-0 top-0 h-full w-full md:w-[440px] bg-background-50 overflow-y-auto animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5">
          <p className="text-xs uppercase tracking-widest text-foreground-500">
            Cleaner profile
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-5 pb-6 pt-3">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <img
              src={cleaner.image}
              alt={cleaner.name}
              className="w-16 h-16 rounded-full object-cover object-top"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-xl text-foreground-950 truncate">
                  {cleaner.name}
                </h3>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusPill[cleaner.status]}`}
                >
                  {statusLabel[cleaner.status]}
                </span>
              </div>
              <p className="text-xs text-foreground-600 mt-0.5">
                {cleaner.role} · {cleaner.city}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mt-5">
            <div className="bg-background-100 rounded-lg px-3 py-2.5 text-center">
              <p className="font-heading text-lg text-foreground-950">{cleaner.jobs}</p>
              <p className="text-[10px] text-foreground-600 uppercase tracking-wider">
                Jobs
              </p>
            </div>
            <div className="bg-background-100 rounded-lg px-3 py-2.5 text-center">
              <p className="font-heading text-lg text-foreground-950 flex items-center justify-center gap-1">
                {cleaner.rating > 0 ? (
                  <>
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    {cleaner.rating.toFixed(2)}
                  </>
                ) : (
                  <span className="text-foreground-500">—</span>
                )}
              </p>
              <p className="text-[10px] text-foreground-600 uppercase tracking-wider">
                Rating
              </p>
            </div>
            <div className="bg-background-100 rounded-lg px-3 py-2.5 text-center">
              <p className="font-heading text-lg text-foreground-950">{cleaner.city}</p>
              <p className="text-[10px] text-foreground-600 uppercase tracking-wider">
                City
              </p>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-5">
            <a
              href={`mailto:cleaner-${cleaner.id}@purenest.co`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-xs font-medium whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              Message
            </a>
            <a
              href={`tel:+15555550100`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-xs font-medium whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call
            </a>
            <Link
              to="/admin/bookings"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-xs font-medium whitespace-nowrap"
            >
              <i className="ri-calendar-line"></i>
              Bookings
            </Link>
          </div>

          {/* Status quick toggle */}
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
              Status
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {(["active", "onboarding", "on_leave"] as AdminCleaner["status"][]).map(
                (s) => (
                  <button
                    key={s}
                    onClick={() => onStatusChange(s)}
                    className={`flex-1 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      cleaner.status === s
                        ? "bg-background-50 text-foreground-950"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {statusLabel[s]}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Recent jobs */}
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
              Recent jobs
            </p>
            {recent.length === 0 ? (
              <div className="bg-background-100 rounded-lg px-4 py-5 text-center">
                <p className="text-xs text-foreground-600">No jobs assigned yet</p>
              </div>
            ) : (
              <ul className="bg-background-100 rounded-lg divide-y divide-background-200 overflow-hidden">
                {recent.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center gap-3 px-3.5 py-2.5"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-50 text-foreground-700">
                      <i className={`${b.serviceIcon} text-sm`}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground-950 truncate">
                        {b.service}
                      </p>
                      <p className="text-[10px] text-foreground-600 truncate">
                        {b.dateLabel} · {b.time} · {b.customer}
                      </p>
                    </div>
                    <span className="text-[10px] font-medium text-foreground-700 whitespace-nowrap">
                      {b.id}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Danger */}
          <div className="mt-6 pt-4 border-t border-background-200">
            <button
              onClick={onRemove}
              className="inline-flex items-center gap-1.5 text-xs text-accent-700 hover:text-accent-800 cursor-pointer"
            >
              <i className="ri-delete-bin-line"></i>
              Remove from roster
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}